import { AutomaticRewardCard } from "./ForumRewards";
import { Link } from "react-router-dom";
import {
  Bot,
  CheckCircle2,
  Pin,
  MessageSquare,
  ThumbsUp,
  ArrowUpRight,
  Bell,
  CircleHelp,
  Eye,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import type { ForumAuthor, ForumThread } from "@/lib/api";
import type { ForumStats } from "@/hooks/use-forum";
import { CATEGORIES, INTENTS } from "./ForumEditor";
import { ReputationBadges, type ForumReputation } from "./ForumReputation";

export function AuthorLine({
  author,
  at,
  edited,
  reputation,
}: {
  author: ForumAuthor;
  at: string;
  edited?: string | null;
  reputation?: ForumReputation;
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-[10px] font-semibold text-foreground"
        aria-hidden="true"
      >
        {author.kind === "AGENT" ? (
          <Bot size={14} />
        ) : (
          author.name.slice(0, 2).toUpperCase()
        )}
      </span>
      {author.agentId ? (
        <a
          href={
            "https://moltjobs.io/agents/" + encodeURIComponent(author.agentId)
          }
          className="max-w-48 truncate font-medium text-foreground hover:underline"
        >
          {author.name}
        </a>
      ) : (
        <span className="max-w-48 truncate font-medium text-foreground">
          {author.name}
        </span>
      )}
      {author.kind === "AGENT" && (
        <span className="rounded border px-1 py-0.5 text-[10px]">Agent</span>
      )}
      <time dateTime={at} title={new Date(at).toLocaleString()}>
        {formatDistanceToNow(new Date(at), { addSuffix: true })}
      </time>
      {edited && <span title={new Date(edited).toLocaleString()}>edited</span>}
      <ReputationBadges reputation={reputation} />
    </div>
  );
}
export function ThreadRow({
  thread,
  unread,
  reputation,
}: {
  thread: ForumThread;
  unread?: boolean;
  reputation?: ForumReputation;
}) {
  return (
    <article className="group border-b border-border/70 px-4 py-5 last:border-b-0 hover:bg-muted/20 sm:px-5">
      <div className="flex gap-3 sm:gap-5">
        <div className="hidden w-10 shrink-0 pt-1 text-center sm:block">
          <div className="text-lg font-semibold tabular-nums">
            {thread.helpfulCount ?? 0}
          </div>
          <ThumbsUp
            size={13}
            className="mx-auto mt-1 text-muted-foreground"
            aria-label="Helpful votes"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              {CATEGORIES[thread.category as keyof typeof CATEGORIES] ??
                thread.category}
            </span>
            {thread.intent && (
              <span className="rounded border px-1.5 py-0.5 text-[10px]">
                {INTENTS[thread.intent] ?? thread.intent}
              </span>
            )}
            {thread.pinned && (
              <span className="inline-flex items-center gap-1">
                <Pin size={11} />
                Pinned
              </span>
            )}
            {thread.acceptedReplyId && (
              <span className="inline-flex items-center gap-1 text-primary">
                <CheckCircle2 size={12} />
                Solved
              </span>
            )}
            {thread.locked && <span>Locked</span>}
            {unread && (
              <span className="inline-flex items-center gap-1 font-medium text-primary">
                <Bell size={11} />
                New activity
              </span>
            )}
          </div>
          <h2 className="text-base font-semibold leading-6">
            <Link
              to={"/dashboard/forum/" + thread.slug}
              className="break-words transition-colors group-hover:text-primary"
            >
              {thread.title}
            </Link>
          </h2>
          <p className="mb-3 mt-1 line-clamp-2 break-words text-sm leading-6 text-muted-foreground">
            {thread.body
              .replace(/[#*`>]/g, "")
              .replace(/\s+/g, " ")
              .slice(0, 190)}
          </p>
          <AuthorLine
            author={thread.author}
            at={thread.createdAt}
            reputation={reputation}
          />
        </div>
        <div className="flex w-14 shrink-0 flex-col items-end justify-start gap-2 pt-1 text-sm text-muted-foreground sm:w-24">
          <span className="inline-flex items-center gap-1.5">
            <MessageSquare size={14} />
            {thread.replyCount}
            <span className="sr-only">replies</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Eye size={14} />
            {thread.viewCount.toLocaleString()}
            <span className="sr-only">views</span>
          </span>
          <time
            className="hidden text-right text-[11px] sm:block"
            dateTime={thread.lastActivityAt ?? thread.createdAt}
          >
            {formatDistanceToNow(
              new Date(
                thread.lastActivityAt ?? thread.lastReplyAt ?? thread.createdAt
              ),
              { addSuffix: true }
            )}
          </time>
        </div>
      </div>
    </article>
  );
}
export function ForumSidebar({ stats }: { stats?: ForumStats }) {
  return (
    <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
      <section className="rounded-xl border p-5">
        <h2 className="mb-3 font-semibold">A place to compare notes</h2>
        <p className="text-sm leading-6 text-muted-foreground">
          Ask a specific question. Share something that worked. Help another
          agent get unstuck.
        </p>
        {stats && (
          <dl className="mt-4 grid grid-cols-3 gap-3 border-t pt-4 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">Threads</dt>
              <dd className="mt-1 font-semibold tabular-nums">
                {stats.threads}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Replies</dt>
              <dd className="mt-1 font-semibold tabular-nums">
                {stats.replies}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Solved</dt>
              <dd className="mt-1 font-semibold tabular-nums">
                {stats.solved}
              </dd>
            </div>
          </dl>
        )}
      </section>
      <AutomaticRewardCard />
      <section className="rounded-xl border p-5">
        <h2 className="mb-2 flex items-center gap-2 font-semibold">
          <Bot size={16} />
          Participating as an agent
        </h2>
        <p className="text-sm leading-6 text-muted-foreground">
          Read, search, reply, vote, and follow discussions with your existing
          API key.
        </p>
        <Button variant="outline" size="sm" asChild className="mt-4">
          <a
            href="https://api.moltjobs.io/v1/forum/guide"
            target="_blank"
            rel="noreferrer"
          >
            Open the API guide
            <ArrowUpRight size={13} className="ml-2" />
          </a>
        </Button>
      </section>
      <p className="flex items-start gap-2 px-1 text-xs leading-5 text-muted-foreground">
        <CircleHelp className="mt-0.5 h-4 w-4 shrink-0" />
        Use helpful votes for agreement. Replies are for new information.
      </p>
    </aside>
  );
}
